import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  Copy,
  Check,
  Instagram,
  Tag,
  ScrollText,
  RefreshCw,
  FileText,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

const CATEGORY_COLOR = {
  "عمل": "#0E5C4A",
  "أحوال شخصية": "#7c3aed",
  "مرور": "#0ea5e9",
  "إيجار": "#d97706",
  "تجاري": "#0f766e",
  "استهلاك": "#dc2626",
  "جنائي": "#1f2937",
  "جزائي": "#1f2937",
  "عقاري": "#b45309",
  "شركات": "#1e40af",
  "عام": "#475569",
};

function IdeaCard({ idea, isAr, idx }) {
  const [copied, setCopied] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [drafted, setDrafted] = useState(null); // {id, title}

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1200);
    } catch {
      toast.error(isAr ? "تعذّر النسخ" : "Copy failed");
    }
  };

  const convertToArticle = async () => {
    setDrafting(true);
    try {
      const r = await api.post(
        "/admin/social/draft-from-idea",
        {
          hook: idea.hook,
          caption: idea.caption,
          law_reference: idea.law_reference,
          category: idea.category,
          hashtags: idea.hashtags,
        },
        { timeout: 120000 }
      );
      setDrafted({ id: r.data?.id, title: r.data?.title });
      toast.success(
        isAr
          ? "تم إنشاء مسوّدة مقال — راجعها قبل النشر"
          : "Article draft created — review before publishing"
      );
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(
        typeof d === "string"
          ? d
          : isAr
          ? "فشل إنشاء المسوّدة"
          : "Draft creation failed"
      );
    } finally {
      setDrafting(false);
    }
  };

  const fullText =
    `${idea.hook}\n\n${idea.caption}\n\n` +
    (idea.law_reference ? `${idea.law_reference}\n\n` : "") +
    (idea.hashtags || []).join(" ");

  return (
    <article
      data-testid={`social-idea-${idea.id}`}
      className="border border-slate-200 bg-white p-6 flex flex-col gap-4 transition-colors hover:border-slate-900"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="shrink-0 font-display text-2xl leading-none"
              style={{ color: "var(--gold-deep)" }}
            >
              {String(idx + 1).padStart(2, "0")}
            </span>
            <span
              className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 text-white"
              style={{
                background:
                  CATEGORY_COLOR[idea.category] || CATEGORY_COLOR["عام"],
              }}
            >
              {idea.category}
            </span>
          </div>
          <h3
            className="font-display text-lg leading-snug tracking-tight"
            dir="rtl"
          >
            {idea.hook}
          </h3>
        </div>
      </header>

      <p
        className="text-sm leading-relaxed text-slate-700 whitespace-pre-line"
        dir="rtl"
      >
        {idea.caption}
      </p>

      {idea.law_reference ? (
        <div
          className="border-s-2 ps-3 py-1 text-xs text-slate-600 leading-relaxed"
          style={{ borderColor: "var(--gold-deep)" }}
          dir="rtl"
        >
          <ScrollText className="w-3 h-3 inline me-1 align-text-bottom" />
          {idea.law_reference}
        </div>
      ) : null}

      {idea.hashtags && idea.hashtags.length > 0 && (
        <div className="flex flex-wrap gap-1.5" dir="ltr">
          {idea.hashtags.map((h, i) => (
            <span
              key={i}
              className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-700"
            >
              {h}
            </span>
          ))}
        </div>
      )}

      <footer className="mt-auto pt-3 border-t border-slate-100 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => copy(idea.caption, `cap-${idea.id}`)}
          data-testid={`copy-caption-${idea.id}`}
          className="rounded-sm h-9"
        >
          {copied === `cap-${idea.id}` ? (
            <Check className="w-3.5 h-3.5 me-1.5 text-emerald-600" />
          ) : (
            <Copy className="w-3.5 h-3.5 me-1.5" />
          )}
          {isAr ? "انسخ النص" : "Copy caption"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => copy(fullText, `full-${idea.id}`)}
          data-testid={`copy-full-${idea.id}`}
          className="rounded-sm h-9"
        >
          {copied === `full-${idea.id}` ? (
            <Check className="w-3.5 h-3.5 me-1.5 text-emerald-600" />
          ) : (
            <Copy className="w-3.5 h-3.5 me-1.5" />
          )}
          {isAr ? "انسخ الكل" : "Copy all"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            copy(
              (idea.hashtags || []).join(" "),
              `tags-${idea.id}`
            )
          }
          data-testid={`copy-tags-${idea.id}`}
          className="rounded-sm h-9"
        >
          {copied === `tags-${idea.id}` ? (
            <Check className="w-3.5 h-3.5 me-1.5 text-emerald-600" />
          ) : (
            <Tag className="w-3.5 h-3.5 me-1.5" />
          )}
          {isAr ? "الوسوم" : "Hashtags"}
        </Button>

        {/* Convert-to-article action sits on its own row so the
            distinction between "share on IG" and "publish on blog"
            is visually obvious. */}
        <div className="basis-full" />
        {drafted ? (
          <Link
            to="/admin/blog-drafts"
            data-testid={`view-draft-${idea.id}`}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm text-xs uppercase tracking-[0.16em] border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {isAr ? "تم — افتح المسوّدة للمراجعة" : "Draft created — review it"}
          </Link>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={convertToArticle}
            disabled={drafting}
            data-testid={`draft-article-${idea.id}`}
            className="btn-gold rounded-sm h-9 px-3"
          >
            {drafting ? (
              <Loader2 className="w-3.5 h-3.5 me-1.5 animate-spin" />
            ) : (
              <FileText className="w-3.5 h-3.5 me-1.5" />
            )}
            {drafting
              ? isAr
                ? "جارٍ كتابة المقال..."
                : "Writing article…"
              : isAr
              ? "حوّل لمقال على المدوّنة"
              : "Convert to blog article"}
          </Button>
        )}
      </footer>
    </article>
  );
}

export default function AdminSocialTopics() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [ideas, setIdeas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const [count, setCount] = useState(15);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/social/generate-topics", null, {
        params: { count },
        timeout: 120000,
      });
      setIdeas(r.data?.ideas || []);
      setMeta({
        count: r.data?.count || 0,
        sample: r.data?.based_on_sample || 0,
        generated_at: r.data?.generated_at || "",
      });
      toast.success(
        isAr
          ? `تم توليد ${r.data?.count || 0} موضوع`
          : `Generated ${r.data?.count || 0} ideas`
      );
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(
        typeof d === "string"
          ? d
          : isAr
          ? "فشل التوليد. حاول مرة أخرى."
          : "Generation failed."
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login?next=/admin/social-topics" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div
      data-testid="admin-social-topics-page"
      className="max-w-7xl mx-auto px-6 lg:px-10 py-20"
    >
      <Link
        to="/admin"
        data-testid="back-to-dashboard"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 transition-colors mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {isAr ? "← لوحة التحكم" : "← Dashboard"}
      </Link>

      <div className="grid lg:grid-cols-12 gap-12 mb-10">
        <div className="lg:col-span-7">
          <p className="eyebrow inline-flex items-center gap-2">
            <Instagram className="w-3.5 h-3.5" style={{ color: "var(--gold-deep)" }} />
            {isAr ? "محتوى السوشل ميديا" : "Social-media briefs"}
          </p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {isAr ? "أفكار جاهزة للنشر." : "Ideas ready to record."}
          </h1>
        </div>
        <div className="lg:col-span-5 lg:pt-4">
          <p className="text-base text-slate-600 leading-relaxed">
            {isAr
              ? "اضغط الزر وأنا أُجهّز لك مجموعة من المواضيع القانونية الكويتية القابلة للنشر مباشرة على إنستغرام — كل فكرة بعنوان جذاب، نص مكتوب، مرجع قانوني، ووسوم. هذه الصفحة خاصة بك ولا تظهر للزوار."
              : "Hit generate and I will produce a batch of ready-to-shoot Kuwaiti-law briefs for your Instagram — each with a hook, a full caption, a legal citation and hashtags. Admin-only."}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-8 border-b border-slate-200 pb-6">
        <div className="inline-flex border border-slate-200 bg-white rounded-sm overflow-hidden">
          {[10, 15, 20].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`social-count-${n}`}
              onClick={() => setCount(n)}
              className={`px-4 h-11 text-xs uppercase tracking-[0.18em] border-s first:border-s-0 border-slate-200 transition-colors ${
                count === n ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {n}
            </button>
          ))}
        </div>

        <Button
          onClick={generate}
          disabled={busy}
          data-testid="generate-social-topics"
          className="btn-gold rounded-sm h-11 px-6"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 me-2 animate-spin" />
              {isAr ? "جارٍ التوليد..." : "Generating…"}
            </>
          ) : (
            <>
              {ideas.length > 0 ? (
                <RefreshCw className="w-4 h-4 me-2" />
              ) : (
                <Sparkles className="w-4 h-4 me-2" />
              )}
              {ideas.length > 0
                ? isAr
                  ? `جدّد ${count} موضوع`
                  : `Regenerate ${count}`
                : isAr
                ? `ولّد ${count} موضوع`
                : `Generate ${count} ideas`}
            </>
          )}
        </Button>

        {meta ? (
          <p className="ms-auto text-xs text-slate-500" data-testid="social-meta">
            {isAr ? "آخر توليد:" : "Last:"}{" "}
            {new Date(meta.generated_at).toLocaleString()}
            {meta.sample > 0
              ? ` · ${isAr ? "مستفاد من" : "based on"} ${meta.sample} ${
                  isAr ? "سؤال حقيقي" : "real questions"
                }`
              : ""}
          </p>
        ) : null}
      </div>

      {busy && ideas.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-20 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" style={{ color: "var(--gold-deep)" }} />
          <p className="text-sm text-slate-600">
            {isAr
              ? "جارٍ التحضير... قد يستغرق دقيقة لـ 15 موضوع."
              : "Working on it… may take up to a minute for 15 ideas."}
          </p>
        </div>
      ) : ideas.length === 0 ? (
        <div
          className="border border-dashed border-slate-300 p-20 text-center text-slate-500"
          data-testid="social-empty"
        >
          <Sparkles className="w-6 h-6 mx-auto mb-3" style={{ color: "var(--gold-deep)" }} />
          <p className="text-sm">
            {isAr
              ? "اضغط «ولّد 15 موضوع» لبدء الجلسة الإبداعية."
              : "Press 'Generate' to start a creative session."}
          </p>
        </div>
      ) : (
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="social-ideas-grid"
        >
          {ideas.map((idea, i) => (
            <IdeaCard key={idea.id} idea={idea} isAr={isAr} idx={i} />
          ))}
        </div>
      )}
    </div>
  );
}
